import { useEffect, useState } from 'react';

import { usePinnedPages } from '@hotjar/webapp/domains/heatmaps/hooks/usePinnedPages';
import { useNotify } from '@hotjar/webapp/hooks/useNotify';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import type {
  SidebarItem as SidebarUiItem,
  State,
} from '@hotjar/webapp/features/Heatmaps/SideBar/SideBar';
import type { Site } from '@hotjar/site';
import { trackPinnedPage } from '@hotjar/heatmaps';
import { usePermission } from '@hotjar/webapp/domains/authorization';
import { mark, measureAndLog } from '@hotjar/performance';
import { MARKS } from '@hotjar/webapp/performance';
import {
  EVENT_NAMES,
  EVENT_UI_POSITIONS,
  EVENT_TRIGGERED_ON,
  EVENT_TRIGGERS,
} from '@hotjar/webapp/domains/heatmaps/tracking';
import type { HeatmapParams } from '@hotjar/heatmaps';
import type { GlobalFilter } from '@hotjar/global-filters';
import {
  convertFromNewUiToQueryFormat,
  convertFromQueryToNewUiFormat,
} from '@hotjar/global-filters';
import { useTranslation } from 'react-i18next';

const getQueryState = (status: string): State => {
  switch (status) {
    case 'loading':
      return 'loading';
    case 'error':
      return 'failed';
    case 'success':
      return 'ready';
    default:
      return 'idle';
  }
};

export interface SidebarItem extends Omit<HeatmapAPI.PinnedPage, 'createdBy'>, SidebarUiItem {}

const mapPinnedToListItem =
  (url: string, targeting: TargetingValue, jsonQueryString: string) =>
  (pinned: Omit<HeatmapAPI.PinnedPage, 'createdBy'>): SidebarItem => ({
    ...pinned,
    isActive:
      pinned.pattern === url &&
      pinned.matchOperation === targeting &&
      pinned.jsonQueryString === jsonQueryString,
  });

const sortByTitle = (itemA: SidebarItem, itemB: SidebarItem): number =>
  itemA.name.localeCompare(itemB.name);

export interface HeatmapSidebarFunctions {
  onItemClick: (item: SidebarItem) => void;
  onItemSave: (item: SidebarItem) => void;
  onItemRemove: (item: SidebarItem) => void;
  onItemEdit: () => void;
  itemsPinned: SidebarItem[];
  itemsPopular: SidebarItem[];
  itemsPopularState: State;
  itemsPinnedState: State;
  isReadOnly: boolean;
}

// eslint-disable-next-line func-params-args/func-params
export const useHeatmapSidebar = (
  currentSite: Site,
  currentUrl: string,
  currentTargeting: TargetingValue,
  currentFilters: GlobalFilter,
  onUpdateParams: (paramUpdates: Partial<HeatmapParams>) => void,
): HeatmapSidebarFunctions => {
  const { t } = useTranslation('heatmaps');
  const { id: siteId } = currentSite;
  const hasHeatmapsUpdatePermission = usePermission('heatmaps-update');
  const hasHeatmapsReadPermission = usePermission('heatmaps-read');

  const track = useTrack();
  const { info, error } = useNotify();

  const [itemsPinned, setItemsPinned] = useState<SidebarItem[]>([]);

  const jsonQueryString = JSON.stringify(convertFromNewUiToQueryFormat(currentFilters));

  const { removePin, updatePin, searchPins, getAllPins } = usePinnedPages({
    siteId,
    pattern: currentUrl,
    matchOperation: currentTargeting,
    jsonQueryString,
  });

  const { data: itemsPinnedData, status: pinSearchStatus } = getAllPins;

  const { refetch: refetchSearch } = searchPins;

  const trackPin = (eventName: string) => {
    if (currentSite) {
      track.click(eventName, EVENT_UI_POSITIONS.SIDE_BAR, track.getGroupDataForSite(currentSite), {
        state_event_was_triggered_on: EVENT_TRIGGERED_ON.SIDE_BAR,
      });
    }
  };

  const onItemClick = (itemClicked: SidebarItem): void => {
    trackPin(EVENT_NAMES.OPEN_HEATMAP);
    const pinned = itemsPinnedData?.find((item) => item.id === itemClicked.id);
    if (pinned) {
      const filters = convertFromQueryToNewUiFormat(JSON.parse(pinned.jsonQueryString));
      onUpdateParams({ url: itemClicked.pattern, match: pinned.matchOperation, filters });
    }
  };

  const onItemSave = (itemSaved: SidebarItem): void => {
    const pinned = itemsPinnedData?.find((item) => item.id === itemSaved.id);

    if (pinned) {
      updatePin.mutateAsync(
        {
          siteId,
          ...pinned,
          name: itemSaved.name,
        },
        {
          onSuccess: () => {
            setItemsPinned((items) =>
              items
                .map((item) => {
                  if (item.id === itemSaved.id) {
                    return { ...item, name: itemSaved.name };
                  }
                  return item;
                })
                .sort(sortByTitle),
            );
            info(t('sidebar.rename-success'));
            trackPinnedPage({
              ...pinned,
              name: itemSaved.name,
              trigger: EVENT_TRIGGERS.SIDE_BAR,
              displayType: 'screenshot',
              eventType: 'edited',
              siteId,
            });
          },
          onError: () => error(t('sidebar.rename-error')),
        },
      );
    }
  };

  const onItemRemove = (itemRemoved: SidebarItem): void => {
    trackPin(EVENT_NAMES.REMOVE_HEATMAP);
    const pinned = itemsPinnedData?.find((item) => item.id === itemRemoved.id);

    if (pinned) {
      removePin.mutateAsync(
        { siteId, ...pinned },
        {
          onSuccess: () => {
            refetchSearch({ cancelRefetch: true });
            setItemsPinned((items) =>
              items.filter((item) => item.id !== pinned.id).sort(sortByTitle),
            );
            info(t('sidebar.remove-success'));
            trackPinnedPage({
              ...pinned,
              trigger: EVENT_TRIGGERS.SIDE_BAR,
              displayType: 'screenshot',
              eventType: 'deleted',
              siteId,
            });
          },
          onError: () => error(t('sidebar.remove-success')),
        },
      );
    }
  };

  const onItemEdit = () => {
    track.click(
      EVENT_NAMES.EDIT_HEATMAP,
      EVENT_UI_POSITIONS.SIDE_BAR,
      track.getGroupDataForSite(currentSite),
      {
        state_event_was_triggered_on: EVENT_TRIGGERED_ON.SIDE_BAR,
      },
    );
  };

  const itemsPopular: SidebarItem[] = [];
  const itemsPopularState = 'ready' as State;
  const itemsPinnedState = getQueryState(pinSearchStatus);

  useEffect(() => {
    // We want itemsPinnedData but empty response is still valid response and UI should reflect it
    if (itemsPinnedData) {
      setItemsPinned(
        itemsPinnedData
          .map(mapPinnedToListItem(currentUrl, currentTargeting, jsonQueryString))
          .sort(sortByTitle),
      );
    }
  }, [siteId, itemsPinnedData, currentUrl, currentTargeting, jsonQueryString]);

  useEffect(() => {
    // Perf: Mark sidebar fetching time

    if (itemsPinnedState === 'loading') {
      mark(MARKS.HEATMAPS_SIDEBAR_FETCH_START);
    }

    if (itemsPinnedState === 'ready') {
      measureAndLog({
        name: MARKS.HEATMAPS_SIDEBAR_FETCH_TIME,
        startMark: MARKS.HEATMAPS_SIDEBAR_FETCH_START,
        addMark: true,
      });
    }
  }, [itemsPinnedState]);

  return {
    onItemClick,
    onItemSave,
    onItemRemove,
    onItemEdit,
    itemsPinned,
    itemsPopular,
    itemsPopularState,
    itemsPinnedState,
    isReadOnly: hasHeatmapsReadPermission && !hasHeatmapsUpdatePermission,
  };
};
